import React, { CSSProperties, useCallback, useContext, useEffect, useState } from 'react';
import { Button, Dropdown, MenuProps, message, Modal, Popconfirm, Space, Table, Tooltip } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EllipsisOutlined,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { MainBreadcrumbItem } from '../../MainBreadcrumb/MainBreadcrumb';
import { SelectedMenuContext } from '../../../contexts/selectedMenu.context';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import { fetcher } from '../../../extras/fetcher';
import { CourierAutomationTripInterface } from '../../../types/courierAutomationTrip.interface';
import { myDateSorter, myNumberSorter } from '../../../extras/sorters';
import { myDateFormat } from '../../../extras/myDateFormat';
import { TableHeader } from '../Ikea/Stan/TableHeader';
import { TableFooter } from '../Ikea/Stan/TableFooter';
import { PermError } from '../NotFoundPage/PermError';
import { ModalAddAutomationTrip } from './ModalAddAutomationTrip';
import './Courier.css';

interface Props {
  handleSetMainTitle: (title: string) => void;
  setBreadcrumbHandler: (items: MainBreadcrumbItem[] | null) => void;
}

const statusStyle: Record<'checked' | 'unchecked', CSSProperties> = {
  checked: {color: 'green'},
  unchecked: {color: 'red'},
};

const StatusIcon = ({value}: {value: boolean | number}) => Boolean(value)
  ? <CheckOutlined style={statusStyle.checked}/>
  : <CloseOutlined style={statusStyle.unchecked}/>;

export const CourierAutomation = ({
  handleSetMainTitle,
  setBreadcrumbHandler,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [permError, setPermError] = useState<boolean>(false);
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [tableData, setTableData] = useState<CourierAutomationTripInterface[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const selectedMenuContext = useContext(SelectedMenuContext);
  const setSelectedMenuKey = selectedMenuContext?.setSelectedKey;
  const {windowHeight} = useWindowDimensions();

  const getData = useCallback(async () => {
    setLoading(true);
    const response = await fetcher('courier/automation');
    setLoading(false);

    if (response.status === 403) {
      setPermError(true);
      return;
    }

    if (response.status >= 400 || !Array.isArray(response.result)) {
      messageApi.error('Nie udało się pobrać listy automatyzacji.');
      return;
    }

    setPermError(false);
    setTableData(response.result);
  }, [messageApi]);

  useEffect(() => {
    void getData();
    const refreshInterval = window.setInterval(() => void getData(), 15000);
    return () => window.clearInterval(refreshInterval);
  }, [getData]);

  useEffect(() => {
    const breadcrumbItems: MainBreadcrumbItem[] = [{
      href: '/courier/automation',
      key: 'courier_2',
      label: 'Wysyłki Kurierskie 2',
    }];

    handleSetMainTitle('Wysyłki Kurierskie 2');
    setBreadcrumbHandler(breadcrumbItems);
    setSelectedMenuKey?.(['courier_2']);
  }, [handleSetMainTitle, setBreadcrumbHandler, setSelectedMenuKey]);

  const changePrinted = async (record: CourierAutomationTripInterface) => {
    const printed = !Boolean(record.printed);
    setUpdatingId(record.id);
    const response = await fetcher(
      `courier/automation/${record.id}/printed`,
      'PATCH',
      {printed},
    );
    setUpdatingId(null);

    if (response.status === 403) {
      messageApi.error('Nie posiadasz wymaganych uprawnień!');
      return;
    }

    if (response.status >= 400) {
      messageApi.error(response.result?.message || 'Nie udało się zmienić statusu wydruku.');
      return;
    }

    setTableData((current) => current.map((trip) =>
      trip.id === record.id ? response.result : trip,
    ));
    messageApi.success(
      printed
        ? `Trip ${record.trip} oznaczono jako wydrukowany.`
        : `Trip ${record.trip} oznaczono jako niewydrukowany.`,
    );
  };

  const changeSelectedPrinted = async (printed: boolean) => {
    const selectedTrips = tableData.filter((trip) =>
      selectedRowKeys.includes(trip.id),
    );

    if (selectedTrips.length === 0) {
      return;
    }

    setBulkLoading(true);
    const responses = await Promise.all(selectedTrips.map((trip) => fetcher(
      `courier/automation/${trip.id}/printed`,
      'PATCH',
      {printed},
    )));
    setBulkLoading(false);

    const failedResponses = responses.filter((response) => response.status >= 400);
    await getData();
    setSelectedRowKeys([]);

    if (failedResponses.some((response) => response.status === 403)) {
      messageApi.error('Nie posiadasz wymaganych uprawnień!');
      return;
    }

    if (failedResponses.length > 0) {
      messageApi.error(
        `Nie udało się zmienić statusu dla ${failedResponses.length} z ${selectedTrips.length} tripów.`,
      );
      return;
    }

    messageApi.success(
      printed
        ? `Oznaczono ${selectedTrips.length} tripów jako wydrukowane.`
        : `Oznaczono ${selectedTrips.length} tripów jako niewydrukowane.`,
    );
  };

  const deleteSelectedTrips = async () => {
    const selectedTrips = tableData.filter((trip) =>
      selectedRowKeys.includes(trip.id),
    );

    if (selectedTrips.length === 0) {
      return;
    }

    setBulkLoading(true);
    const responses = await Promise.all(selectedTrips.map((trip) => fetcher(
      `courier/automation/${trip.id}`,
      'DELETE',
    )));
    setBulkLoading(false);

    const failedResponses = responses.filter((response) => response.status >= 400);
    await getData();
    setSelectedRowKeys([]);

    if (failedResponses.some((response) => response.status === 403)) {
      messageApi.error('Nie posiadasz wymaganych uprawnień!');
      return;
    }

    if (failedResponses.length > 0) {
      messageApi.error(
        `Nie udało się usunąć ${failedResponses.length} z ${selectedTrips.length} tripów.`,
      );
      return;
    }

    messageApi.success(`Usunięto ${selectedTrips.length} tripów z listy automatyzacji.`);
  };

  const deleteTrip = async (record: CourierAutomationTripInterface) => {
    setDeletingId(record.id);
    const response = await fetcher(
      `courier/automation/${record.id}`,
      'DELETE',
    );
    setDeletingId(null);

    if (response.status === 403) {
      messageApi.error('Nie posiadasz wymaganych uprawnień!');
      return;
    }

    if (response.status >= 400) {
      messageApi.error(response.result?.message || 'Nie udało się usunąć tripa.');
      return;
    }

    setTableData((current) => current.filter((trip) => trip.id !== record.id));
    setSelectedRowKeys((current) => current.filter((key) => key !== record.id));
    messageApi.success(`Usunięto trip ${record.trip} z listy automatyzacji.`);
  };

  const confirmDeleteTrip = (record: CourierAutomationTripInterface) => {
    Modal.confirm({
      title: 'Usunięcie tripa',
      content: `Czy na pewno usunąć trip ${record.trip}?`,
      okText: 'Usuń',
      okButtonProps: {danger: true},
      cancelText: 'Anuluj',
      centered: true,
      onOk: () => deleteTrip(record),
    });
  };

  const bulkMenuItems: MenuProps['items'] = [
    {
      key: 'printed',
      label: 'Oznacz jako wydrukowane',
      icon: <CheckOutlined style={statusStyle.checked}/>,
    },
    {
      key: 'not-printed',
      label: 'Oznacz jako niewydrukowane',
      icon: <CloseOutlined style={statusStyle.unchecked}/>,
    },
    {type: 'divider'},
    {
      key: 'delete',
      label: 'Usuń',
      icon: <DeleteOutlined/>,
      danger: true,
    },
  ];

  const handleBulkMenuClick: MenuProps['onClick'] = ({key}) => {
    const selectedCount = selectedRowKeys.length;

    if (key === 'printed') {
      Modal.confirm({
        title: 'Oznaczenie tripów jako wydrukowane',
        content: `Zmienić status ${selectedCount} zaznaczonych tripów?`,
        okText: 'Zmień',
        cancelText: 'Anuluj',
        onOk: () => changeSelectedPrinted(true),
      });
      return;
    }

    if (key === 'not-printed') {
      Modal.confirm({
        title: 'Oznaczenie tripów jako niewydrukowane',
        content: 'Przygotowane tripy wrócą do kolejki automatycznego druku. Kontynuować?',
        okText: 'Zmień',
        cancelText: 'Anuluj',
        onOk: () => changeSelectedPrinted(false),
      });
      return;
    }

    Modal.confirm({
      title: 'Usunięcie tripów z automatyzacji',
      content: `Czy na pewno usunąć ${selectedCount} zaznaczonych tripów?`,
      okText: 'Usuń',
      okButtonProps: {danger: true},
      cancelText: 'Anuluj',
      centered: true,
      onOk: deleteSelectedTrips,
    });
  };

  const columns: ColumnsType<CourierAutomationTripInterface> = [
    {
      title: 'Trip',
      dataIndex: 'trip',
      width: 180,
      align: 'center',
      sorter: (a, b) => myNumberSorter(a.trip, b.trip),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Data dodania',
      dataIndex: 'createdAt',
      width: 180,
      align: 'center',
      sorter: (a, b) => myDateSorter(a.createdAt, b.createdAt),
      sortDirections: ['descend', 'ascend'],
      render: (value) => myDateFormat(value),
    },
    {
      title: 'Przygotowany do druku',
      dataIndex: 'prepare',
      width: 220,
      align: 'center',
      render: (value) => <StatusIcon value={value}/>,
    },
    {
      title: 'Wydrukowany',
      dataIndex: 'printed',
      width: 180,
      align: 'center',
      render: (value, record) => {
        const nextPrinted = !Boolean(record.printed);
        const confirmation = nextPrinted
          ? `Oznaczyć trip ${record.trip} jako wydrukowany?`
          : `Oznaczyć trip ${record.trip} jako niewydrukowany? Przygotowany trip wróci do kolejki druku.`;

        return <Popconfirm
          title="Zmiana statusu wydruku"
          description={confirmation}
          okText="Zmień"
          cancelText="Anuluj"
          onConfirm={() => changePrinted(record)}
        >
          <Tooltip title="Kliknij, aby zmienić status">
            <Button
              type="text"
              aria-label={`Zmień status wydruku tripa ${record.trip}`}
              disabled={updatingId !== null || bulkLoading}
              icon={updatingId === record.id
                ? <LoadingOutlined/>
                : <StatusIcon value={value}/>
              }
            />
          </Tooltip>
        </Popconfirm>;
      },
    },
    {
      title: 'Usuń',
      key: 'delete',
      width: 80,
      align: 'center',
      render: (_, record) =>
        <Tooltip title="Usuń trip">
          <Button
            type="text"
            danger
            aria-label={`Usuń trip ${record.trip}`}
            icon={<DeleteOutlined/>}
            loading={deletingId === record.id}
            disabled={deletingId !== null || bulkLoading}
            onClick={() => confirmDeleteTrip(record)}
          />
        </Tooltip>,
    },
  ];

  if (permError) {
    return <PermError/>;
  }

  return <>
    <Table
      className="courier-table"
      rowKey="id"
      columns={columns}
      dataSource={tableData}
      loading={loading}
      rowSelection={{
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
      }}
      pagination={false}
      bordered
      size="small"
      scroll={{y: windowHeight * .6}}
      style={{opacity: .9}}
      title={() => <TableHeader
        getData={getData}
        selectedCount={selectedRowKeys.length}
        additional={<Space size="small" style={{marginLeft: 8}}>
          <Button
            icon={<PlusOutlined/>}
            onClick={() => setAddModalOpen(true)}
          >Dodaj trip</Button>
          <Dropdown
            menu={{items: bulkMenuItems, onClick: handleBulkMenuClick}}
            trigger={['click']}
            placement="bottomLeft"
            disabled={selectedRowKeys.length === 0 || bulkLoading}
          >
            <Tooltip title={selectedRowKeys.length === 0
              ? 'Zaznacz tripy, aby wyświetlić operacje'
              : 'Operacje dla zaznaczonych tripów'
            }>
              <Button
                aria-label="Operacje dla zaznaczonych tripów"
                icon={<EllipsisOutlined/>}
                disabled={selectedRowKeys.length === 0 || bulkLoading}
                loading={bulkLoading}
              />
            </Tooltip>
          </Dropdown>
        </Space>}
      />}
      footer={() => <TableFooter
        selectedCount={selectedRowKeys.length}
        totalCount={tableData.length}
      />}
    />
    <ModalAddAutomationTrip
      open={addModalOpen}
      onClose={() => setAddModalOpen(false)}
      onAdded={getData}
    />
    {contextHolder}
  </>;
};

